import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Menu, X, Mail, Phone, Clock, MapPin, Building2, ArrowRight,
  Check, ShieldCheck
} from 'lucide-react';

/* ---------- Logo ---------- */
export function LogoMedAliados({ tone = "dark" }) {
  return (
    <Link to="/" className="flex items-center group" aria-label="MedAliados">
      <img
        src="/medaliados.png"
        alt="MedAliados"
        className="h-10 w-auto object-contain"
        style={{ filter: tone === "light" ? "brightness(0) invert(1)" : "none" }}
      />
    </Link>
  );
}

export function LogoMedSenior({ tone = "dark" }) {
  return (
    <span className="flex items-center">
      <img
        src="/medsenior.png"
        alt="MedSênior"
        className="h-7 w-auto object-contain"
        style={{ filter: tone === "light" ? "brightness(0) invert(1)" : "none" }}
      />
    </span>
  );
}

/* ---------- Button ---------- */
export function Button({ children, variant = "primary", size = "md", onClick, to, icon: IconComp, className = "" }) {
  const base = "inline-flex items-center justify-center gap-2 font-medium rounded-full transition-all duration-200 select-none whitespace-nowrap";
  const sizes = { sm: "px-4 py-2 text-sm", md: "px-6 py-3 text-[0.95rem]", lg: "px-8 py-4 text-[1.02rem]" };
  const variants = {
    primary: "bg-verde-medio text-fundo-branco hover:bg-verde-escuro shadow-soft hover:shadow-card",
    outline: "border border-verde-escuro/30 text-verde-escuro hover:border-verde-escuro hover:bg-verde-escuro hover:text-fundo-branco",
    ghost: "text-verde-escuro hover:bg-verde-escuro/5",
    onDark: "bg-fundo-branco text-verde-escuro hover:bg-dourado hover:text-verde-escuro",
    outlineDark: "border border-fundo-branco/40 text-fundo-branco hover:border-fundo-branco hover:bg-fundo-branco hover:text-verde-escuro",
  };
  const cls = `${base} ${sizes[size]} ${variants[variant]} ${className}`;
  if (to) {
    return <Link to={to} onClick={onClick} className={cls}>{children}{IconComp && <IconComp size={16} />}</Link>;
  }
  return <button onClick={onClick} className={cls}>{children}{IconComp && <IconComp size={16} />}</button>;
}

/* ---------- Eyebrow ---------- */
export function Eyebrow({ children, tone = "dark" }) {
  const color = tone === "light" ? "text-verde-lima" : "text-verde-medio";
  return (
    <div className={`eyebrow flex items-center gap-2 ${color}`}>
      <span className="inline-block h-px w-6 bg-current opacity-50" />
      <span>{children}</span>
    </div>
  );
}

/* ---------- Reveal ---------- */
export function Reveal({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            setTimeout(() => el.classList.add("in"), delay);
            io.unobserve(el);
          }
        });
      },
      { threshold: 0.12 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [delay]);
  return <div ref={ref} className={`reveal ${className}`}>{children}</div>;
}

/* ---------- Section ---------- */
export function Section({ children, bg = "bege", className = "", id }) {
  const bgs = {
    bege: "bg-fundo-bege",
    branco: "bg-fundo-branco",
    escuro: "bg-verde-escuro text-fundo-branco",
    gradient: "hero-grid",
  };
  return (
    <section id={id} className={`${bgs[bg]} ${className}`}>
      <div className="mx-auto max-w-[1200px] px-6 md:px-10">{children}</div>
    </section>
  );
}

/* ---------- SectionHeader ---------- */
export function SectionHeader({ eyebrow, title, sub, align = "left", tone = "dark" }) {
  return (
    <div className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""}`}>
      {eyebrow && <Eyebrow tone={tone}>{eyebrow}</Eyebrow>}
      <h2 className={`font-display font-bold h-section mt-4 text-balance ${tone === "light" ? "text-fundo-branco" : "text-verde-escuro"}`}>{title}</h2>
      {sub && <p className={`mt-5 text-[1.08rem] leading-relaxed ${tone === "light" ? "text-fundo-branco/75" : "text-texto-medio"}`}>{sub}</p>}
    </div>
  );
}

/* ---------- PageBanner ---------- */
export function PageBanner({ eyebrow, title, sub }) {
  return (
    <section className="hero-grid pt-[120px] pb-16 md:pt-[160px] md:pb-24 border-b border-borda/70">
      <div className="mx-auto max-w-[1200px] px-6 md:px-10">
        <Reveal>
          {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
          <h1 className="font-display font-bold h-hero mt-5 text-verde-escuro max-w-4xl text-balance">{title}</h1>
          {sub && <p className="mt-6 text-[1.15rem] md:text-[1.25rem] text-texto-medio max-w-2xl leading-relaxed">{sub}</p>}
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- CTAStrip ---------- */
export function CTAStrip({ headline, sub, label = "Fale com Nossa Equipe" }) {
  return (
    <section className="bg-verde-escuro text-fundo-branco">
      <div className="mx-auto max-w-[1200px] px-6 md:px-10 py-20 md:py-24">
        <div className="grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <Eyebrow tone="light">Próximo passo</Eyebrow>
            <h2 className="font-display font-bold h-section mt-4 text-balance">{headline}</h2>
            {sub && <p className="mt-4 text-fundo-branco/75 max-w-2xl text-[1.05rem] leading-relaxed">{sub}</p>}
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Button variant="onDark" size="lg" icon={ArrowRight} to="/contato">{label}</Button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- StatBar ---------- */
export function StatBar({ items }) {
  return (
    <div className="bg-verde-escuro text-fundo-branco">
      <div className="mx-auto max-w-[1200px] px-6 md:px-10 py-5 md:py-7">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4">
          {items.map((it, i) => (
            <div key={i} className="flex flex-col items-start md:items-center md:text-center border-l md:border-l-0 md:border-r last:border-r-0 border-fundo-branco/15 pl-5 md:pl-0 md:px-4">
              <div className="font-display font-bold text-[2rem] md:text-[2.4rem] leading-none text-verde-lima">{it.value}</div>
              <div className="mt-2 text-[0.86rem] uppercase tracking-wider text-fundo-branco/65 font-mono">{it.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- Header ---------- */
const navLinks = [
  { to: "/", label: "Home" },
  { to: "/quem-somos", label: "Quem Somos" },
  { to: "/como-funciona", label: "Como Funciona" },
  { to: "/para-corretoras", label: "Para Corretoras" },
  { to: "/contato", label: "Contato" },
];

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 20);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <header className={`hdr fixed top-0 inset-x-0 z-50 border-b ${scrolled ? "bg-fundo-bege/85 backdrop-blur-md border-borda/70" : "bg-transparent border-transparent"}`}>
      <div className="mx-auto max-w-[1200px] px-6 md:px-10 h-[72px] flex items-center justify-between">
        <LogoMedAliados />

        <nav className="nav-desktop flex items-center gap-1">
          {navLinks.map(({ to, label }) => (
            <Link key={to} to={to}
              className={`px-3.5 py-2 text-[0.92rem] rounded-full transition-colors ${
                location.pathname === to
                  ? "text-verde-escuro font-semibold"
                  : "text-texto-medio hover:text-verde-escuro"
              }`}>
              {label}
              {location.pathname === to && <span className="block h-[2px] w-4 mx-auto mt-0.5 rounded-full bg-verde-medio" />}
            </Link>
          ))}
          <span className="mx-3 h-5 w-px bg-borda" />
          <Button size="sm" icon={ArrowRight} to="/contato">Cadastre-se</Button>
        </nav>

        <button className="nav-mobile inline-flex h-10 w-10 items-center justify-center rounded-full border border-borda bg-fundo-branco"
          onClick={() => setOpen((o) => !o)} aria-label="Menu">
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className="nav-mobile bg-fundo-bege border-t border-borda px-6 py-4 space-y-1">
          {navLinks.map(({ to, label }) => (
            <Link key={to} to={to}
              className={`block w-full text-left px-3 py-3 rounded-lg text-[1rem] ${
                location.pathname === to
                  ? "bg-verde-escuro text-fundo-branco"
                  : "text-texto-escuro hover:bg-fundo-branco"
              }`}>
              {label}
            </Link>
          ))}
          <div className="pt-2">
            <Button size="md" className="w-full" icon={ArrowRight} to="/contato">Cadastre Sua Corretora</Button>
          </div>
        </div>
      )}
    </header>
  );
}

/* ---------- Footer ---------- */
export function Footer() {
  return (
    <footer className="bg-verde-escuro text-fundo-branco">
      <div className="mx-auto max-w-[1200px] px-6 md:px-10 pt-20 pb-10">
        <div className="grid md:grid-cols-12 gap-12 pb-12 border-b border-fundo-branco/15">
          <div className="md:col-span-5 space-y-5">
            <LogoMedAliados tone="light" />
            <p className="text-fundo-branco/70 max-w-sm text-[0.95rem] leading-relaxed">
              Plataforma digital para escalar a operação comercial de corretoras no mercado sênior. Tecnologia, previsibilidade e portfólio de alta demanda.
            </p>
            <div className="pt-3 flex items-center gap-4">
              <span className="font-mono text-[10px] tracking-widest uppercase text-fundo-branco/50">Parceria estratégica</span>
              <span className="h-4 w-px bg-fundo-branco/20" />
              <LogoMedSenior tone="light" />
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="eyebrow text-verde-lima mb-4">Navegação</div>
            <ul className="space-y-2.5 text-fundo-branco/80">
              {navLinks.map(({ to, label }) => (
                <li key={to}><Link to={to} className="hover:text-verde-lima transition-colors">{label}</Link></li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="eyebrow text-verde-lima mb-4">Contato Comercial</div>
            <ul className="space-y-3 text-fundo-branco/80 text-[0.95rem]">
              <li className="flex items-start gap-3"><Mail size={16} className="mt-1 text-verde-lima" /> contato@medaliados.com.br</li>
              <li className="flex items-start gap-3"><Phone size={16} className="mt-1 text-verde-lima" /> (11) 3000-0000</li>
              <li className="flex items-start gap-3"><Clock size={16} className="mt-1 text-verde-lima" /> Seg a Sex · 9h às 18h</li>
              <li className="flex items-start gap-3"><MapPin size={16} className="mt-1 text-verde-lima" /> São Paulo, SP</li>
            </ul>
          </div>
        </div>

        <div className="pt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-[0.83rem] text-fundo-branco/55">
          <div>© 2026 MedAliados. Todos os direitos reservados.</div>
          <div className="flex items-center gap-2">
            <Building2 size={14} />
            <span>Plataforma exclusiva para corretoras com CNPJ ativo</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
